import { Button } from '@/components/ui/button';
import { Icon } from '@/lib/icons';
import type { ThemeImportItem } from '@/api/theme-import-history.contract';
import type { ThemeImportQuestionsDialogProps } from './ThemeImportQuestionsDialog';
import { ThemeImportRow } from './ThemeImportRow';

interface ThemeImportsTableProps {
  rows: ThemeImportItem[];
  isLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  loadedCount: string;
  more: string;
  loadingMore: string;
  loading: string;
  noFiles: string;
  labels: {
    colFile: string;
    colTheme: string;
    colSector: string;
    colQuestions: string;
    colAuthor: string;
    colDate: string;
    colStatus: string;
    colActions: string;
    unknownAuthor: string;
    active: string;
    inactive: string;
    previewQuestions: string;
    editQuestions: string;
    updateQuestions: string;
    delete: string;
    cancel: string;
    deleteTitle: string;
    deleteDescription: string;
    questionsDialog: ThemeImportQuestionsDialogProps['copy'];
  };
  isDeletingId: string | null;
  onDelete: (id: string) => void;
  onLoadMore: () => void;
}

export function ThemeImportsTable({
  rows,
  isLoading,
  hasNextPage,
  isFetchingNextPage,
  loadedCount,
  more,
  loadingMore,
  loading,
  noFiles,
  labels,
  isDeletingId,
  onDelete,
  onLoadMore,
}: ThemeImportsTableProps) {
  return (
    <section className="pt-7">
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Icon name="refreshCw" className="mr-3 h-5 w-5 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">{loading}</span>
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Icon name="fileText" className="mb-3 h-8 w-8 text-muted-foreground/80" />
          <p className="text-sm text-muted-foreground">{noFiles}</p>
        </div>
      ) : (
        <div className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,0.8fr)_minmax(0,0.5fr)_minmax(0,0.9fr)_minmax(0,0.85fr)_minmax(0,0.5fr)_auto] gap-x-4">
          <div className="col-span-full grid grid-cols-subgrid items-center border-b border-border/40 pb-3">
            <span className="text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{labels.colFile}</span>
            <span className="text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{labels.colTheme}</span>
            <span className="text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{labels.colSector}</span>
            <span className="text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{labels.colQuestions}</span>
            <span className="text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{labels.colAuthor}</span>
            <span className="text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{labels.colDate}</span>
            <span className="text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{labels.colStatus}</span>
            <span className="text-right text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{labels.colActions}</span>
          </div>
          {rows.map((row) => (
            <ThemeImportRow
              key={row.id}
              row={row}
              labels={labels}
              isDeleting={isDeletingId === row.id}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}

      <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
        <span>{loadedCount}</span>
        {hasNextPage && (
          <Button
            variant="outline"
            disabled={isFetchingNextPage}
            onClick={onLoadMore}
            className="rounded-full border-border/40 bg-transparent text-xs font-semibold text-foreground/80 hover:bg-muted/20 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-35"
          >
            {isFetchingNextPage ? loadingMore : more}
          </Button>
        )}
      </div>
    </section>
  );
}
